import { useCallback, useMemo, useState } from 'react';
import { Copy, Check, Terminal, KeyRound } from 'lucide-react';
import { NavLink } from 'react-router';

import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/routes';
import { orgPath } from '@/utils/org-path';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';

interface UploadApiDocsSectionProps {
  orgId: string;
  collectionId: string;
  /** Field names the collection allows callers to override per file
   *  (collection.exposed_upload_attributes). Empty when none are configured. */
  exposedAttributes?: string[];
}

/**
 * Derive the platform-api base URL from the current window origin.
 *
 * Naming convention:
 *   platform-dev.capitol.ai   -> platform-api-development.capitol.ai
 *   platform-hmg.capitol.ai   -> platform-api-hmg.capitol.ai
 *   platform.capitol.ai       -> platform-api.capitol.ai
 *   ey.capitol.ai             -> platform-api-staging.capitol.ai (EY uses staging API)
 *   localhost                  -> platform-api-staging.capitol.ai
 *
 * Kept in sync with external-chat/api-docs-section.tsx. See that file's TODO
 * about replacing this hostname→API env mapping with a Vite env var.
 */
function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://platform-api.capitol.ai';
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'https://platform-api-staging.capitol.ai';
  }

  // platform-dev -> platform-api-development
  if (hostname === 'platform-dev.capitol.ai') {
    return 'https://platform-api-development.capitol.ai';
  }

  // platform-{env}.capitol.ai -> platform-api-{env}.capitol.ai
  const envMatch = hostname.match(/^platform-(\w+)\.capitol\.ai$/);
  if (envMatch) {
    return `https://platform-api-${envMatch[1]}.capitol.ai`;
  }

  // platform.capitol.ai (prod) -> platform-api.capitol.ai
  if (hostname === 'platform.capitol.ai') {
    return 'https://platform-api.capitol.ai';
  }

  // Fallback for other subdomains (e.g. ey.capitol.ai)
  return 'https://platform-api-staging.capitol.ai';
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard unavailable in insecure context */
      },
    );
  }, [text]);

  return (
    <Button variant='outline' size='sm' onClick={handleCopy}>
      {copied ? (
        <>
          <Check className='size-3.5 mr-1' />
          Copied!
        </>
      ) : (
        <>
          <Copy className='size-3.5 mr-1' />
          Copy
        </>
      )}
    </Button>
  );
}

/** Static Python batch uploader — copy-paste ready. Loops a folder + a
 *  manifest.csv (filename,<field1>,<field2>,...), applies per-file
 *  field_overrides, and reports OK/FAIL per file. Rendered verbatim in a code
 *  block. */
const PYTHON_BATCH_SCRIPT = `import csv, mimetypes, os, sys, requests

API = os.environ["CAPITOL_API_BASE"]        # e.g. https://platform-api.capitol.ai (see curl examples above)
PAT = os.environ["CAPITOL_PAT"]              # pat_... from Settings > API tokens
ORG = sys.argv[1]; COLLECTION = sys.argv[2]  # orgid, collectionId
FOLDER = sys.argv[3]                         # local folder of files
MANIFEST = sys.argv[4]                       # manifest.csv: filename,<field1>,<field2>,...

H = {"Authorization": f"Bearer {PAT}"}

def upload_one(path, fields):
    size = os.path.getsize(path)
    ctype = mimetypes.guess_type(path)[0] or "application/octet-stream"
    init = requests.post(
        f"{API}/public/uploads/{ORG}/{COLLECTION}/multipart/init",
        headers=H,
        json={"filename": os.path.basename(path), "total_size": size,
              "content_type": ctype, "field_overrides": fields},
        timeout=30,
    )
    init.raise_for_status()
    info = init.json()
    parts, part_size = [], info["part_size"]
    with open(path, "rb") as f:
        for pu in info["part_urls"]:
            chunk = f.read(part_size)
            r = requests.put(pu["url"], data=chunk, timeout=300)
            r.raise_for_status()
            parts.append({"part_number": pu["part_number"], "etag": r.headers["ETag"]})
    done = requests.post(
        f"{API}/public/uploads/{ORG}/{COLLECTION}/multipart/{info['upload_id']}/complete",
        headers=H, json={"parts": parts}, timeout=60,
    )
    done.raise_for_status()

with open(MANIFEST) as fh:
    for row in csv.DictReader(fh):
        fname = row.pop("filename")
        path = os.path.join(FOLDER, fname)
        try:
            upload_one(path, {k: v for k, v in row.items() if v != ""})
            print(f"OK   {fname}")
        except Exception as e:
            print(f"FAIL {fname}: {e}")   # continue; fix the row and re-run (re-uploads by filename)
`;

export function UploadApiDocsSection({
  orgId,
  collectionId,
  exposedAttributes = [],
}: UploadApiDocsSectionProps) {
  const { isUserApiTokensEnabled } = useLaunchDarkly();
  const apiBase = useMemo(() => getApiBaseUrl(), []);
  const org = orgId || 'YOUR_ORG_ID';
  const collection = collectionId || 'YOUR_COLLECTION_ID';

  // Build the field_overrides example. Prefer the collection's exposed
  // attributes so the docs show the exact keys this collection accepts;
  // otherwise fall back to a generic placeholder.
  const hasExposedAttributes = exposedAttributes.length > 0;
  const fieldOverridesExample = useMemo(() => {
    if (hasExposedAttributes) {
      const entries = exposedAttributes
        .map((name) => `      "${name}": "..."`)
        .join(',\n');
      return `{\n${entries}\n    }`;
    }
    return `{"your_field": "value"}`;
  }, [exposedAttributes, hasExposedAttributes]);

  const initCurl = `curl -X POST \\
  ${apiBase}/public/uploads/${org}/${collection}/multipart/init \\
  -H "Authorization: Bearer pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "filename": "report.pdf",
    "total_size": 1048576,
    "content_type": "application/pdf",
    "field_overrides": ${fieldOverridesExample}
  }'`;

  const putCurl = `curl -X PUT "<part_urls[0].url>" \\
  --upload-file part-0.bin`;

  const completeCurl = `curl -X POST \\
  ${apiBase}/public/uploads/${org}/${collection}/multipart/<upload_id>/complete \\
  -H "Authorization: Bearer pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{"parts": [{"part_number": 1, "etag": "\\"abc123\\""}]}'`;

  const singlePartCurl = `curl -X POST \\
  ${apiBase}/public/uploads/${org}/${collection} \\
  -H "Authorization: Bearer pat_..." \\
  -F "files=@report.pdf" \\
  -F 'field_overrides=${fieldOverridesExample.replace(/\n\s*/g, ' ')}'`;

  const sampleCurl = `curl -X GET \\
  ${apiBase}/public/collections/${org}/${collection}/points/by-external-id/<external_id>/sample \\
  -H "Authorization: Bearer pat_..."`;

  return (
    <div className='space-y-6'>
      {/* Intro */}
      <div className='flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3'>
        <Terminal className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
        <div className='space-y-2'>
          <p className='text-sm leading-relaxed text-muted-foreground'>
            Upload files to this collection over the API, attaching per-file
            custom fields as you go. Requests are authenticated with a{' '}
            <strong>Personal API token</strong> (a{' '}
            <code className='rounded bg-muted px-1 py-0.5 text-xs'>pat_</code>{' '}
            token) scoped to your user, so each upload is attributed to you.
          </p>
          {/* Only link to the tokens page when it's actually reachable (same
              flag guards the route); otherwise the link 404s on FeatureGuard. */}
          {isUserApiTokensEnabled && (
            <Button asChild variant='outline' size='sm'>
              <NavLink to={orgPath(ROUTES.ACCOUNT.API_TOKENS, orgId)}>
                <KeyRound className='size-3.5 mr-1' />
                Create a token in Settings → API tokens
              </NavLink>
            </Button>
          )}
        </div>
      </div>

      {/* 3-step multipart flow */}
      <div className='space-y-4'>
        <h4 className='text-sm font-semibold'>Multipart upload — 3 steps</h4>

        {/* Step 1 */}
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <h5 className='text-sm font-medium'>1. Initialize the upload</h5>
            <CopyButton text={initCurl} />
          </div>
          <p className='text-xs text-muted-foreground'>
            POST the file metadata and any per-file{' '}
            <code className='rounded bg-muted px-1 py-0.5'>
              field_overrides
            </code>
            . The response returns an{' '}
            <code className='rounded bg-muted px-1 py-0.5'>upload_id</code>, the{' '}
            <code className='rounded bg-muted px-1 py-0.5'>part_size</code>, and
            a list of presigned{' '}
            <code className='rounded bg-muted px-1 py-0.5'>part_urls</code>.
          </p>
          <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{initCurl}</code>
          </pre>
          {hasExposedAttributes ? (
            <p className='text-xs text-muted-foreground'>
              This collection accepts overrides for:{' '}
              {exposedAttributes.map((name, i) => (
                <span key={name}>
                  {i > 0 && ', '}
                  <code className='rounded bg-muted px-1 py-0.5'>{name}</code>
                </span>
              ))}
              .
            </p>
          ) : (
            <p className='text-xs text-muted-foreground'>
              The overridable fields are configured per collection. Ask a
              collection admin which field names are exposed for this
              collection.
            </p>
          )}
        </div>

        {/* Step 2 */}
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <h5 className='text-sm font-medium'>2. Upload each part</h5>
            <CopyButton text={putCurl} />
          </div>
          <p className='text-xs text-muted-foreground'>
            PUT each chunk of the file directly to its presigned S3 URL. Capture
            the <code className='rounded bg-muted px-1 py-0.5'>ETag</code>{' '}
            response header for each part — you&apos;ll need it to complete the
            upload.
          </p>
          <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{putCurl}</code>
          </pre>
        </div>

        {/* Step 3 */}
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <h5 className='text-sm font-medium'>3. Complete the upload</h5>
            <CopyButton text={completeCurl} />
          </div>
          <p className='text-xs text-muted-foreground'>
            POST the ordered list of{' '}
            <code className='rounded bg-muted px-1 py-0.5'>part_number</code> /{' '}
            <code className='rounded bg-muted px-1 py-0.5'>etag</code> pairs to
            finalize the file and kick off processing.
          </p>
          <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
            <code>{completeCurl}</code>
          </pre>
        </div>
      </div>

      {/* Single-part fallback — the only working upload path for orgs with
          app-level encryption, where multipart init is blocked. */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-semibold'>
            Single-request upload (required for encrypted organizations)
          </h4>
          <CopyButton text={singlePartCurl} />
        </div>
        <p className='text-xs text-muted-foreground'>
          Organizations with app-level encryption cannot use the multipart flow
          (the <code className='rounded bg-muted px-1 py-0.5'>init</code> call
          returns 400). Upload files in a single{' '}
          <code className='rounded bg-muted px-1 py-0.5'>
            multipart/form-data
          </code>{' '}
          request instead — same PAT auth, same{' '}
          <code className='rounded bg-muted px-1 py-0.5'>field_overrides</code>,
          capped at 100 MB per file. Also handy for small files when you
          don&apos;t want the 3-step flow.
        </p>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{singlePartCurl}</code>
        </pre>
      </div>

      {/* Field discovery */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-semibold'>
            Discover current field values
          </h4>
          <CopyButton text={sampleCurl} />
        </div>
        <p className='text-xs text-muted-foreground'>
          To see the field values currently stored for an already-uploaded file,
          GET a sample point by its external id.
        </p>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{sampleCurl}</code>
        </pre>
      </div>

      {/* Python batch script */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-semibold'>
            Batch upload script (Python)
          </h4>
          <CopyButton text={PYTHON_BATCH_SCRIPT} />
        </div>
        <p className='text-xs text-muted-foreground'>
          Uploads every file in a folder using a{' '}
          <code className='rounded bg-muted px-1 py-0.5'>manifest.csv</code>{' '}
          (header:{' '}
          <code className='rounded bg-muted px-1 py-0.5'>
            filename,&lt;field1&gt;,&lt;field2&gt;,...
          </code>
          ). Set{' '}
          <code className='rounded bg-muted px-1 py-0.5'>CAPITOL_API_BASE</code>{' '}
          and <code className='rounded bg-muted px-1 py-0.5'>CAPITOL_PAT</code>,
          then run{' '}
          <code className='rounded bg-muted px-1 py-0.5'>
            python upload.py &lt;orgid&gt; &lt;collectionId&gt; ./files
            manifest.csv
          </code>
          .
        </p>
        <pre className='rounded-md bg-muted p-4 text-xs font-mono overflow-x-auto whitespace-pre'>
          <code>{PYTHON_BATCH_SCRIPT}</code>
        </pre>
      </div>
    </div>
  );
}
